/*
Which operators and params a dynamic CodeRef filter may use, per attribute type.

The filter builder uses these rules to decide what to offer, and the server uses them to decide what
to accept when an attribute definition is saved.

Mirrors Statoil.TI.CommonLibrary.Entities/Attributes/ReferenceFilterRules.cs (keep in sync).
Members below appear in the same order as that file so the two can be read side by side. If the two
disagree, the builder offers rules the server rejects and the user cannot save.
 */

import { AttributeType } from '@/shared/helpers/api/types/entities.ts';
import { ReferenceFilterOperator, ReferenceFilterValue } from '@/shared/codeedit/dynamicFilterTypes.ts';

// Attribute types that behave the same way in a filter. 'Unknown' means no group applies: either the
// type is not known yet, or the type has no group and cannot be filtered on at all.
// These strings are stored in the predicate as ReferenceFilterNode.group, so they must stay identical
// to the TypeGroup enum tokens in ReferenceFilterRules.cs (keep in sync).
export const typeGroups = ['Text', 'Numeric', 'Bool', 'CodeRef', 'Temporal', 'Unknown'] as const;
export type TypeGroup = typeof typeGroups[number];

export function typeGroup(type: AttributeType | null): TypeGroup {
    switch (type) {
        case 'String': case 'Uri': return 'Text';
        case 'Int': case 'Float': return 'Numeric';
        case 'Bool': return 'Bool';
        case 'CodeRef': case 'LibraryRef': return 'CodeRef';
        case 'Date': case 'DateTime': return 'Temporal';
        default: return 'Unknown';
    }
}

export function isFilterableType(type: AttributeType | null): boolean {
    return type === null || typeGroup(type) !== 'Unknown';
}

export const operatorsByGroup: Partial<Record<TypeGroup, ReferenceFilterOperator[]>> = {
    Text: ['Eq', 'Neq', 'Contains', 'Prefix', 'Suffix', 'Exact', 'In'],
    Numeric: ['Eq', 'Neq', 'Gt', 'Lt', 'Ge', 'Le', 'In'],
    Bool: ['Eq', 'Neq'],
    CodeRef: ['Eq', 'Neq', 'In'],
    Temporal: ['Eq', 'Neq', 'Gt', 'Lt', 'Ge', 'Le']
};

// Code fields a filter can refer to that are not attribute definitions.
const builtInFieldTypes: Record<string, AttributeType> = {
    name: 'String',
    description: 'String',
    isvalid: 'Bool'
};

export const builtInFields = ['name', 'description', 'isValid'];

export function builtInFieldType(attributeName: string): AttributeType | null {
    return builtInFieldTypes[attributeName.toLowerCase()] ?? null;
}

// An unknown reference field constrains nothing, so every operator stays available. Any other group
// must declare its operators, so one added without an operator list fails instead of accepting every operator.
export function operatorIsAllowedForGroup(op: ReferenceFilterOperator, refGroup: TypeGroup): boolean {
    if (refGroup === 'Unknown') return true;
    return operatorsByGroup[refGroup]?.includes(op) ?? false;
}

// A param is comparable only when it is the same type group as the reference field
export function paramIsComparable(paramType: AttributeType | null, refGroup: TypeGroup): boolean {
    return refGroup === 'Unknown' || typeGroup(paramType) === refGroup;
}

function isNumeric(value: string): boolean {
    return value.trim() !== '' && Number.isFinite(Number(value));
}

function isBool(value: string): boolean {
    return value.toLowerCase() === 'true' || value.toLowerCase() === 'false';
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

// Reduces a date or date-time to "YYYY-MM-DDTHH:mm:ss", or null if it is not a shape we can compare.
// A date-only value is padded out so that it can be compared against a date-time as text, which is
// what lets a "2024-01-01" filter match a "2024-01-01T00:00:00" option value.
//
// Its server counterparts are IsTemporal in ReferenceFilterRules.cs, which only needs the yes/no half
// to validate a stored filter, and TryParseTemporal in ReferenceFilterEvaluator.cs, which needs the
// parsed value itself. All three accept exactly the same shapes (keep in sync).
export function normalizeTemporal(value: ReferenceFilterValue): string | null {
    if (typeof value !== 'string') return null;

    const parts = value.trim().split(/[T ]/);
    if (parts.length > 2) return null;

    const [date, time = '00:00:00'] = parts;
    if (!DATE.test(date) || !TIME.test(time)) return null;

    // Sub-second precision is dropped, and "HH:mm" is padded to "HH:mm:ss", so that every value
    // ends up the same width and can be compared as text
    const [hoursMinutesSeconds] = time.split('.');
    const paddedTime = hoursMinutesSeconds.length === 5 ? hoursMinutesSeconds + ':00' : hoursMinutesSeconds;

    const normalized = `${date}T${paddedTime}`;

    // The patterns above only check the shape. Dates might not be real or can roll over and silently
    // match the wrong values
    const parsed = new Date(`${normalized}Z`);
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 19) !== normalized) return null;

    return normalized;
}

function isTemporal(value: string): boolean {
    return normalizeTemporal(value) !== null;
}

// An unknown reference field constrains nothing, so any fixed value is accepted
function singleLiteralError(value: string | null, refGroup: TypeGroup): string {
    if (value === null || value.trim() === '') return 'Must have a value';

    switch (refGroup) {
        case 'Numeric': return isNumeric(value) ? '' : 'Numbers only';
        case 'Bool': return isBool(value) ? '' : 'Must be true or false';
        case 'Temporal': return isTemporal(value) ? '' : 'Must be a date';
        default: return '';
    }
}

// Validates a fixed value against the reference field's type; empty string means valid
export function literalValueError(
    value: string | null,
    values: string[] | null,
    refGroup: TypeGroup,
    op: ReferenceFilterOperator
): string {
    if (op === 'In') {
        if (values === null || values.length === 0) return 'Must have at least one value';
        return values.map(item => singleLiteralError(item, refGroup)).find(error => error !== '') ?? '';
    }

    if (values !== null) return 'Only In takes a list of values';

    return singleLiteralError(value, refGroup);
}
